import React, { useState, useEffect } from 'react';
import realTimeDeviceService from '../../services/realTimeDeviceService';
import RTActionEditor from './RTActionEditor';
import '../assets/AllTabsCommon.css';
import '../assets/StartRemoteDebug.css';
import '../assets/Home.css';
import '../assets/RemoteDebugSessionTabs.css';

/**
 * Real-Time Device Management — Start (2-step wizard).
 *
 * Step 1: pick device + duration (immediate vs schedule for later).
 * Step 2: configure Monitor/Configure/Run actions (shared RTActionEditor),
 *         then Start Session.
 *
 * On submit it calls realTimeDeviceService.startSession() and asks the parent
 * to switch to the Active tab via onSessionStarted().
 */
function RTStartSession({ userInfo, onSessionStarted, prefilledSystemId, setPrefilledSystemId, hideSystemId }) {
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1 state
  const [systemId, setSystemId] = useState('');
  const [scheduleMode, setScheduleMode] = useState('immediate');
  const [schedulingMode, setSchedulingMode] = useState('FIXED'); // 'FIXED' | 'FLEXIBLE'
  const [durationMins, setDurationMins] = useState('30');
  const [scheduledTime, setScheduledTime] = useState('');
  const [step1Error, setStep1Error] = useState('');

  useEffect(() => {
    if (prefilledSystemId) {
      setSystemId(prefilledSystemId);
      if (!hideSystemId && setPrefilledSystemId) setPrefilledSystemId(null);
    }
  }, [prefilledSystemId, setPrefilledSystemId, hideSystemId]);

  // Step 2 state
  const [actions, setActions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const goToStep2 = () => {
    if (!systemId.trim()) {
      setStep1Error('Device System ID is required.');
      return;
    }
    if (scheduleMode === 'scheduled' && !scheduledTime) {
      setStep1Error('Please choose a date and time to schedule the session.');
      return;
    }
    setStep1Error('');
    setCurrentStep(2);
  };

  const handleAddAction = (action) => setActions((prev) => [...prev, action]);
  const handleRemoveAction = (actionId) => setActions((prev) => prev.filter((a) => a.id !== actionId));

  const handleStartSession = async () => {
    setSubmitting(true);
    setSubmitError('');
    setSuccessMessage('');
    try {
      const created = await realTimeDeviceService.startSession({
        systemId: systemId.trim(),
        durationMins: parseInt(durationMins, 10) || null,
        scheduled: scheduleMode === 'scheduled',
        scheduledTime: scheduleMode === 'scheduled' ? new Date(scheduledTime).toISOString() : null,
        schedulingMode,
        actions,
      });
      const msg = scheduleMode === 'scheduled'
        ? `RT session scheduled successfully!\n\nSession ID: ${created?.sessionId || ''}`
        : `RT session started successfully!\n\nSession ID: ${created?.sessionId || ''}`;
      setSuccessMessage(msg);
      if (onSessionStarted) onSessionStarted(created);
    } catch (err) {
      setSubmitError('Failed to start session. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const step1Valid = systemId.trim() !== '' &&
    (scheduleMode !== 'scheduled' || scheduledTime !== '');

  return (
    <div className="data-table-tab-container">
      <div className="rt-start-step">

        {currentStep === 1 && (
          <>
            <div className="card rt-card">
              <div className="card-header" style={{ marginBottom: 16 }}>
                <div className="rt-step-indicator" style={{ marginBottom: 0 }}>
                  <span className="rt-step active">1 · Device &amp; Duration</span>
                  <span>→</span>
                  <span className="rt-step">2 · Configure Actions</span>
                </div>
              </div>

              {step1Error && <div className="error-banner">{step1Error}</div>}

              {!hideSystemId && (
                <div className="rt-form-field">
                  <label>System ID <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g., 12345 or MDM-5G-NE-001"
                    value={systemId}
                    onChange={(e) => setSystemId(e.target.value)}
                  />
                  <p className="rt-hint">Enter the unique identifier for the target device.</p>
                </div>
              )}

              {!hideSystemId && (
                <div className="rt-form-field">
                  <label>Collector ID</label>
                  <input type="text" value="737629725" readOnly style={{ background: '#f5f5f5', cursor: 'default', color: '#888' }} />
                </div>
              )}

              <div className="rt-form-field">
                <label>Session Duration</label>
                <div className="rt-duration-cards">
                  <button
                    type="button"
                    className={`rt-duration-card ${scheduleMode === 'immediate' ? 'selected' : ''}`}
                    onClick={() => setScheduleMode('immediate')}
                  >
                    <strong>Start Immediately</strong>
                    <span>Begin the session now</span>
                  </button>
                  <button
                    type="button"
                    className={`rt-duration-card ${scheduleMode === 'scheduled' ? 'selected' : ''}`}
                    onClick={() => setScheduleMode('scheduled')}
                  >
                    <strong>Schedule for Later 📅</strong>
                    <span>Set a specific date and time to start</span>
                  </button>
                </div>
              </div>

              {scheduleMode === 'immediate' ? (
                <div className="rt-form-field">
                  <label>Duration (minutes)</label>
                  <input type="number" value={durationMins} onChange={(e) => setDurationMins(e.target.value)} />
                </div>
              ) : (
                <>
                  <div className="rt-form-field">
                    <label>Scheduled Start <span className="required-asterisk">*</span></label>
                    <input type="datetime-local" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} />
                  </div>
                  <div className="rt-form-field">
                    <label>Scheduling Mode</label>
                    <div className="rt-duration-cards">
                      <button
                        type="button"
                        className={`rt-duration-card ${schedulingMode === 'FIXED' ? 'selected' : ''}`}
                        onClick={() => setSchedulingMode('FIXED')}
                      >
                        <strong>Fixed</strong>
                        <span>Fires exactly at scheduled time</span>
                      </button>
                      <button
                        type="button"
                        className={`rt-duration-card ${schedulingMode === 'FLEXIBLE' ? 'selected' : ''}`}
                        onClick={() => setSchedulingMode('FLEXIBLE')}
                      >
                        <strong>Flexible</strong>
                        <span>Auto-starts when device connects</span>
                      </button>
                    </div>
                  </div>
                  <div className="rt-form-field">
                    <label>Duration (minutes)</label>
                    <input type="number" value={durationMins} onChange={(e) => setDurationMins(e.target.value)} />
                  </div>
                </>
              )}
            </div>

            <button type="button" className="submit-button rt-step1-cta" onClick={goToStep2} disabled={!step1Valid}>
              Continue to Setup →
            </button>

            <div className="rt-rd-banner" style={{ marginTop: 16 }}>
              💡 <strong>Tip:</strong> You can add or remove monitoring actions after a session has started.
            </div>
          </>
        )}
      </div>

      {currentStep === 2 && (
        <div className="rt-config-step">
          {/* Meta strip: device + duration on left, step indicator on right */}
          <div className="rt-session-meta" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="rt-text-muted">
              Device: <strong>{systemId}</strong>
              &nbsp;·&nbsp;
              ⏱ <strong>{durationMins} min{scheduleMode === 'scheduled' ? ' (scheduled)' : ''}</strong>
            </span>
            <div className="rt-step-indicator inline">
              <span className="rt-step">1 · Device &amp; Duration</span>
              <span>→</span>
              <span className="rt-step active">2 · Configure Actions</span>
            </div>
          </div>

          {successMessage && (
            <div className="success-banner">
              <div className="success-message">{successMessage}</div>
            </div>
          )}

          {submitError && <div className="error-banner">{submitError}</div>}

          {!successMessage && (
            <>
              <RTActionEditor actions={actions} onAdd={handleAddAction} onRemove={handleRemoveAction} />

              {/* Sticky bottom bar: count left, Back + primary CTA right */}
              <div className="rt-start-bar">
                <span className="rt-action-count">
                  {actions.length} action{actions.length !== 1 ? 's' : ''} configured
                </span>
                <div className="rt-start-bar-actions">
                  <button type="button" className="go-back-button" onClick={() => setCurrentStep(1)}>← Back</button>
                  <button type="button" className="submit-button" onClick={handleStartSession} disabled={submitting}>
                    {submitting ? 'Starting…' : scheduleMode === 'scheduled' ? 'Schedule Session' : 'Start Session'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RTStartSession;
